/**
 * Custom hook for search functionality
 */
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SearchResult } from '../types';

interface UseSearchProps {
  allCases: any[];
  language: string;
  t: (en: string, no: string) => string;
  onNewCase: () => void;
}

export const useSearch = ({ allCases, language, t, onNewCase }: UseSearchProps) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchSelectedIndex, setSearchSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);

  const searchResults = useMemo((): SearchResult[] => {
    const newCaseAction: SearchResult = {
      id: 'new-case',
      title: t('+ New Case', '+ Ny sak'),
      subtitle: t('Create a new case', 'Opprett en ny sak'),
      url: '',
      type: 'action'
    };
    
    if (!searchQuery.trim()) {
      const recentCases = allCases.slice(0, 4).map((c: any) => ({
        id: c.id,
        title: c.topic || t('Untitled', 'Uten tittel'),
        subtitle: c.client_name || '',
        url: `/lexy/case/${c.id}/workspace`,
        type: 'case' as const
      }));
      return [newCaseAction, ...recentCases];
    }
    
    const query = searchQuery.toLowerCase();
    const matchingCases = allCases
      .filter((c: any) => 
        c.topic?.toLowerCase().includes(query) || 
        c.client_name?.toLowerCase().includes(query)
      )
      .slice(0, 5)
      .map((c: any) => ({
        id: c.id,
        title: c.topic || t('Untitled', 'Uten tittel'),
        subtitle: c.client_name || '',
        url: `/lexy/case/${c.id}/workspace`,
        type: 'case' as const
      }));
    
    if (matchingCases.length === 0) {
      return [newCaseAction, { id: 'empty', title: t('No results', 'Ingen resultater'), url: '', type: 'empty' }];
    }
    
    return [newCaseAction, ...matchingCases];
  }, [searchQuery, allCases, t]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showSearchDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSearchSelectedIndex(i => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSearchSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = searchResults[searchSelectedIndex];
      if (selected?.type === 'action') {
        onNewCase();
        setSearchQuery('');
        setShowSearchDropdown(false);
      } else if (selected?.url) {
        navigate(selected.url);
        setSearchQuery('');
        setShowSearchDropdown(false);
      }
    } else if (e.key === 'Escape') {
      setShowSearchDropdown(false);
    }
  }, [showSearchDropdown, searchResults, searchSelectedIndex, onNewCase, navigate]);

  const handleSearchResultClick = useCallback((item: SearchResult) => {
    if (item.type === 'action') {
      onNewCase();
      setSearchQuery('');
      setShowSearchDropdown(false);
    } else if (item.url) {
      navigate(item.url);
      setSearchQuery('');
      setShowSearchDropdown(false);
    }
  }, [onNewCase, navigate]);

  // Click outside handler
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!searchDropdownRef.current?.contains(e.target as Node) && 
          !searchInputRef.current?.contains(e.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    showSearchDropdown,
    setShowSearchDropdown,
    searchSelectedIndex,
    setSearchSelectedIndex,
    searchInputRef,
    searchDropdownRef,
    searchResults,
    handleSearchKeyDown,
    handleSearchResultClick,
  };
};
